import { VisitPatient } from '@dreryk/egabinet-visit-frontend-api';

import { FormContextValues, Translate } from '@dreryk/react-common';
import { StrongText, StyledFlex, TextSeparator, TextSeparatorType } from '@dreryk/react-components';

import { getPatientReferenceIdentifierValueTranslatableText } from '~libs.shared.patient';

import { medicalLeaveFormTranslationKeys } from '../../../../../translations';
import { MedicalLeaveFormSection } from '../../../common/formSections/MedicalLeaveFormSection';
import { MedicalLeaveFormInsurer } from '../../../common/MedicalLeaveFormInsurer';
import { MedicalLeaveFormBean } from '../../medicalLeave.types';

import { PassportIdentityDocument } from './identityDocument/passport/PassportIdentityDocument';
import { PassportIdentityDocumentLoader } from './identityDocument/passport/PassportIdentityDocumentLoader';
import { PatientPersonalDataText } from './PatientPersonalDataText';

const patientExternalIdentifierAsIdentityDocument = (patient: VisitPatient) => {
   return (): JSX.Element => {
      return (
         <TextSeparator separator={TextSeparatorType.COMMA_SPACE}>
            <PatientPersonalDataText patient={patient} />
            <StrongText testId={'externalIdentifier'}>
               <Translate>{getPatientReferenceIdentifierValueTranslatableText(patient.referenceIdentifier)}</Translate>
            </StrongText>
         </TextSeparator>
      );
   };
};

const patientIdentityDocument = (patient: VisitPatient) => {
   return (): JSX.Element => {
      return (
         <PassportIdentityDocumentLoader patientId={patient.id}>
            {({ identityDocument, setPatient }) => (
               <PassportIdentityDocument
                  identityDocument={identityDocument}
                  onIdentityDocumentChanged={setPatient}
                  patient={patient}
               />
            )}
         </PassportIdentityDocumentLoader>
      );
   };
};

type MedicalLeaveFormPatientDataSectionProps = {
   readonly controller: FormContextValues<MedicalLeaveFormBean>;
   readonly patient: VisitPatient;
};

export const MedicalLeaveFormPatientDataSection = (props: MedicalLeaveFormPatientDataSectionProps): JSX.Element => {
   const { patient } = props;
   const patientExternalIdentifier = patient.referenceIdentifier!.findExternalIdentifier()!;

   return (
      <MedicalLeaveFormSection title={medicalLeaveFormTranslationKeys.patientData}>
         <StyledFlex direction={'column'} gap={'8px'}>
            {patientExternalIdentifier.accept<JSX.Element>({
               visitPesel: patientExternalIdentifierAsIdentityDocument(patient),
               visitGuardianPesel: patientExternalIdentifierAsIdentityDocument(patient),
               visitPassport: patientExternalIdentifierAsIdentityDocument(patient),
               visitNationalIdentificationNumber: patientIdentityDocument(patient),
            })}
         </StyledFlex>

         <MedicalLeaveFormInsurer controller={props.controller} label={medicalLeaveFormTranslationKeys.insuredIn} />
      </MedicalLeaveFormSection>
   );
};
